import React, { Key, useEffect, useMemo, useState } from 'react';
import { Flex, Space, Switch, Typography } from 'antd';
import { useQuery } from 'react-query';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { DashboardTypes } from '@shared/utils/constants';
import { standardSort } from '@shared/utils/standardSort';
import { toMoment } from '@shared/utils/date';
import { fixtureTypeToPascalCase } from '@shared/utils/string';
import { formatCurrency } from '@shared/utils/currency';
import type { OutStandingData } from '@api/features/statistics/getOutstandingsData';
import { useAuth } from '@client/lib/auth';
import { getOutstandingsData } from '@client/lib/api';
import Table from '@client/components/Table/Table';
import { stringSetToSortedList } from '@client/utils/stringSetToSortedList';
import { Links } from '@client/utils/links';
import { formatDate } from '@client/utils/formatDate';
import { useDashboardCurrency } from '../helpers/dashboardInfo';
import styles from './OutstandingsDashboard.module.css';

type FilterMaps = {
	contractTypes: string[];
	vessels: string[];
	contracts: string[];
	counterparties: string[];
};

type Props = {
	eua?: boolean;
	title?: string;
	className?: string;
	setLastUpdated?: (m: any) => void;
	rightControls?: React.ReactNode;
	onFilteredRowsChange?: (rows: OutStandingData[]) => void;
	onFilterStateChange?: (state: {
		localFilters?: Record<string, FilterValue | null>;
		showZero: boolean;
	}) => void;
	disableCurrencyFilter?: boolean;
};

export const getColumns = (filterMaps: FilterMaps) => {
	const columns: ColumnsType<OutStandingData> = [
		{
			dataIndex: 'vessel',
			title: 'Vessel',
			render: (vessel) => (
				<Typography.Text className={styles.totalCell}>{vessel.name}</Typography.Text>
			),
			filters: filterMaps.vessels.map((vessel) => ({ text: vessel, value: vessel })),
			filterMultiple: true,
			filterSearch: true,
			key: 'vessel',
			onFilter: (value, record) => record.vessel?.name === value,
		},
		{
			title: 'Contract',
			dataIndex: 'contractIdentifier',
			render: (identifier, row) => (
				<Space size="small">
					<Link to={Links.Voyage.get(row.contractId)}>{identifier}</Link>
				</Space>
			),
			filters: filterMaps.contracts.map((c) => ({ text: c, value: c })),
			filterMultiple: true,
			filterSearch: true,
			key: 'contract',
			onFilter: (value, record) => record.contractIdentifier === value,
		},
		{
			title: 'Contract type',
			dataIndex: 'contractType',
			render: (identifier) => <Space size="small">{fixtureTypeToPascalCase(identifier)}</Space>,
			filters: filterMaps.contractTypes.map((ct) => ({
				// @ts-ignore
				text: fixtureTypeToPascalCase(ct).toString(),
				value: ct,
			})),
			filterMultiple: true,
			filterSearch: true,
			key: 'contractType',
			onFilter: (value, record) => record.contractType === value,
			width: 50,
		},
		{
			title: 'Counterparty',
			dataIndex: 'counterparty',
			render: (cp) => cp?.name ?? '',
			filters: filterMaps.counterparties.map((cp) => ({ text: cp, value: cp })),
			filterMultiple: true,
			filterSearch: true,
			key: 'counterparty',
			onFilter: (value: boolean | Key, record: OutStandingData) => record?.counterparty.name === value,
		},
		{
			title: 'Description',
			dataIndex: 'description',
			render: (d) => d ?? (<em>-</em>)
		},
		{
			title: 'Invoice #',
			dataIndex: 'invoiceNumber',
			render: (n) => n ?? (<em>-</em>),
			sorter: standardSort('invoiceNumber')
		},
		{
			title: 'Invoice date',
			dataIndex: 'invoiceDate',
			render: (d) => (d != null ? formatDate(d) : (<em>-</em>)),
			sorter: (a, b) => {
				if (a.invoiceDate == null && b.invoiceDate == null) {
					return 0;
				}

				if (a.invoiceDate == null) {
					return 1;
				}

				if (b.invoiceDate == null) {
					return -1;
				}

				return toMoment(a.invoiceDate).unix() - toMoment(b.invoiceDate).unix();
			},
		},
		{
			title: 'Due date',
			dataIndex: 'dueDate',
			render: (d) => (d != null ? formatDate(d) : (<em>-</em>)),
			sorter: (a, b) => {
				if (a.dueDate == null && b.dueDate == null) {
					return 0;
				}

				if (a.dueDate == null) {
					return 1;
				}

				if (b.dueDate == null) {
					return -1;
				}

				return toMoment(a.dueDate).unix() - toMoment(b.dueDate).unix();
			},
		},
		{
			title: 'Days outstanding',
			dataIndex: 'daysOutstanding',
			render: (d) => (d != null && d >= 0 ? d : (<em>-</em>)),
			align: 'right',
			sorter: standardSort('daysOutstanding'),
			width: 75
		},
		{
			title: 'Invoiced',
			dataIndex: 'amount',
			render: (amount, row) => (
				<Typography.Text className={styles.totalCell}>
					{formatCurrency(amount, row.currency, { forceDecimals: true, maximumFractionDigits: 2 })}
				</Typography.Text>
			),
			sorter: standardSort('amount'),
			align: 'right',
		},
		{
			title: 'Paid',
			dataIndex: 'paid',
			render: (paid, row) => (
				<Typography.Text className={styles.totalCell}>
					{formatCurrency(paid, row.currency, { forceDecimals: true, maximumFractionDigits: 2 })}
				</Typography.Text>
			),
			sorter: standardSort('paid'),
			align: 'right',
		},
		{
			title: 'Outstanding',
			dataIndex: 'outstanding',
			render: (outstanding, row) => (
				<Typography.Text className={styles.totalCell}>
					{formatCurrency(outstanding, row.currency, { forceDecimals: true, maximumFractionDigits: 2 })}
				</Typography.Text>
			),
			sorter: standardSort('outstanding'),
			align: 'right',
		},
	];

	return columns;
};

const OutstandingsTableSection = ({
	eua = false,
	title = 'Outstandings',
	className,
	setLastUpdated,
	rightControls,
	onFilteredRowsChange,
	onFilterStateChange,
	disableCurrencyFilter = false,
}: Props) => {
	const { userInfo } = useAuth();
	const [localFilters, setLocalFilters] = useState<Record<string, FilterValue | null> | undefined>();
	const [showZero, setShowZero] = useState(false);
	const [sorter, setSorter] = useState<SorterResult<OutStandingData> | null>(null);
	const { data: outstandings, isFetching } = useQuery(
		eua ? DashboardTypes.EUA_OUTSTANDINGS : DashboardTypes.OUTSTANDINGS,
		() => getOutstandingsData(eua),
		{ onSuccess: (d) => setLastUpdated?.(d.lastUpdated), refetchOnWindowFocus: false },
	);

	const currencyCtx = useDashboardCurrency();
	const selectedCurrency = currencyCtx?.currency ?? null;

	const filterMaps = useMemo<FilterMaps>(() => {
		const vessels = new Set<string>();
		const contracts = new Set<string>();
		const contractTypes = new Set<string>();
		const counterparties = new Set<string>();

		outstandings?.data?.forEach((item) => {
			vessels.add(item.vessel.name);
			contracts.add(item.contractIdentifier);
			contractTypes.add(item.contractType?.toString() ?? '');
			if (item.counterparty?.name) {
				counterparties.add(item.counterparty.name);
			}
		});

		return {
			vessels: stringSetToSortedList(vessels),
			contracts: stringSetToSortedList(contracts),
			contractTypes: stringSetToSortedList(contractTypes),
			counterparties: stringSetToSortedList(counterparties),
		};
	}, [outstandings]);

	const localData = useMemo(() => {
		let rows = outstandings?.data ?? [];

		if (userInfo.filterVessels) {
			const ids = new Set(userInfo.relevantVessels.map((v) => v.id));
			rows = rows.filter((r) => ids.has(r.vessel.id));
		}

		if (disableCurrencyFilter === false && selectedCurrency != null) {
			rows = rows.filter((r) => r.currency === selectedCurrency);
		}

		if (localFilters != null) {
			rows = rows.filter((d) => {
				const filterOnContract = localFilters.contract?.length;
				const filterOnCounterparty = localFilters.counterparty?.length;
				const filterOnVessel = localFilters.vessel?.length;
				const filterOnContractType = localFilters.contractType?.length;

				return (
					(filterOnContract ? localFilters.contract!.includes(d.contractIdentifier) : true) &&
					(filterOnVessel ? localFilters.vessel!.includes(d.vessel.name) : true) &&
					(filterOnCounterparty ? localFilters.counterparty!.includes(d.counterparty?.name ?? '') : true) &&
					(filterOnContractType ? localFilters.contractType!.includes(d.contractType?.toString() ?? '') : true)
				);
			});
		}

		if (!showZero) {
			rows = rows.filter((d) => !(d.outstanding >= -0.01 && d.outstanding <= 0.01));
		}

		if (sorter?.order && sorter.field != null) {
			const { field, order } = sorter;

			rows = [...rows].sort((a: OutStandingData, b: OutStandingData) => {
				const aVal = a[field as keyof OutStandingData];
				const bVal = b[field as keyof OutStandingData];

				if (aVal == null) {
					return 1;
				}

				if (bVal == null) {
					return -1;
				}

				if (aVal < bVal) {
					return order === 'ascend' ? -1 : 1;
				}

				if (aVal > bVal) {
					return order === 'ascend' ? 1 : -1;
				}

				return 0;
			});
		}

		return rows;
	}, [outstandings, localFilters, userInfo, showZero, sorter, selectedCurrency]);

	useEffect(() => {
		onFilteredRowsChange?.(localData);
	}, [localData, onFilteredRowsChange]);

	useEffect(() => {
		onFilterStateChange?.({ localFilters, showZero });
	}, [localFilters, showZero, onFilterStateChange]);

	return (
		<>
			<Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
				<Typography.Title level={4} style={{ margin: 0 }}>{title}</Typography.Title>
				<Space align="center">
					{rightControls}
					<Typography.Text>Show zero outstanding?</Typography.Text>
					<Switch checked={showZero} onChange={setShowZero} />
				</Space>
			</Flex>
			<Table
				className={className}
				size="middle"
				onChange={(_pagination, filters, sorter) => {
					setLocalFilters(filters);
					setSorter(sorter as SorterResult<OutStandingData>);
				}}
				scroll={{ x: true }}
				loading={isFetching}
				columns={getColumns(filterMaps)}
				dataSource={localData}
				pagination={{ position: ['bottomCenter'] }}
			/>
		</>
	);
};

export default OutstandingsTableSection;